import React, { useState } from "react";
import { motion } from "framer-motion";
import { X, Sparkles, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const TIER_LABELS = {
  free: "Free",
  foundations: "Foundations",
  accelerator: "Accelerator",
  advanced_accelerator: "Advanced Accelerator",
  elite: "Elite",
  custom_coaching: "Custom Coaching",
};

/**
 * UpgradeGoalsModal — appears when an athlete's tier has goals in the template
 * that they don't yet have on their dashboard. They can accept all/some or skip.
 */
export const UpgradeGoalsModal = ({ tier, missingGoals, onClose, onAccepted }) => {
  // All checked by default — easier to opt out than opt in for high-engagement athletes
  const [selected, setSelected] = useState(
    () => new Set(missingGoals.map((g) => g.title))
  );
  const [saving, setSaving] = useState(false);

  const toggle = (title) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  };

  const accept = async () => {
    setSaving(true);
    try {
      const titles = Array.from(selected);
      const res = await fetch(`${API}/athlete/goals/upgrade-prompt/accept`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ titles }),
      });
      if (!res.ok) throw new Error("Failed");
      const d = await res.json();
      if (d.created > 0) {
        toast.success(`Added ${d.created} new goal${d.created === 1 ? "" : "s"} from your ${TIER_LABELS[tier] || tier} plan`);
      }
      onAccepted();
    } catch {
      toast.error("Couldn't save — try again");
    } finally {
      setSaving(false);
    }
  };

  const dismiss = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/athlete/goals/upgrade-prompt/dismiss`, {
        method: "POST",
        credentials: "include",
      });
      onAccepted();
    } catch {
      toast.error("Couldn't dismiss");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-4"
      onClick={dismiss}
      data-testid="upgrade-goals-modal"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-surface border border-border rounded-2xl w-full max-w-md max-h-[92vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-border flex-shrink-0">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-5 h-5 text-amber-400 flex-shrink-0" />
              <h2 className="font-heading text-lg font-bold text-white">
                New goals for your {TIER_LABELS[tier] || tier} plan
              </h2>
            </div>
            <p className="text-xs text-text-muted">
              Your coach added these for athletes on your tier. Pick the ones you want — skip what doesn't apply.
            </p>
          </div>
          <button onClick={dismiss} disabled={saving} className="text-text-muted hover:text-white flex-shrink-0" data-testid="upgrade-goals-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Goal list */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2" data-testid="upgrade-goals-list">
          {missingGoals.map((g) => {
            const picked = selected.has(g.title);
            return (
              <button
                key={g.title}
                onClick={() => toggle(g.title)}
                className={`w-full text-left rounded-lg border px-3 py-3 transition-colors flex items-center gap-3 ${
                  picked ? "border-amber-500/60 bg-amber-500/5" : "border-border bg-[#0B0F1A] hover:border-amber-500/30"
                }`}
                data-testid={`upgrade-goal-${(g.title || "").replace(/\s+/g, "-").toLowerCase().slice(0, 40)}`}
              >
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                  picked ? "bg-amber-500 border-amber-500" : "border-border"
                }`}>
                  {picked && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white truncate">{g.title}</p>
                  <p className="text-xs text-text-secondary">
                    {g.default_start_value} → {g.default_target_value} {g.unit}
                    <span className="text-text-muted ml-2">({g.goal_type})</span>
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-border flex-shrink-0 flex items-center gap-3">
          <span className="text-xs text-text-muted flex-shrink-0">
            {selected.size}/{missingGoals.length} selected
          </span>
          <button
            onClick={dismiss}
            disabled={saving}
            className="text-sm text-text-secondary hover:text-white px-3 py-2"
            data-testid="upgrade-goals-skip"
          >
            Maybe later
          </button>
          <button
            onClick={accept}
            disabled={saving || selected.size === 0}
            className="flex-1 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
            data-testid="upgrade-goals-accept"
          >
            {saving ? "Adding…" : `Add ${selected.size} goal${selected.size === 1 ? "" : "s"}`}
            {!saving && <ArrowRight className="w-4 h-4" />}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default UpgradeGoalsModal;
